import React, { useCallback, RefObject } from 'react';
import { mapModifiers, ModifierProp } from 'libs/component';

const arrow = '/images/arrow-down.svg';

type ScrollTarget = HTMLElement | string | RefObject<HTMLElement | null> | Window | null | undefined;

export interface ShortcutItemProps {
  title: string;
  subtitle: string;
  onClick?: (event: React.MouseEvent) => void;
  modifiers?: ModifierProp<'active' | 'concept'>;
  scrollElement?: ScrollTarget;
  /** Extra offset in px (e.g. -16). Header height is subtracted automatically at click time. */
  offset?: number;
}

const isRefObject = (value: ScrollTarget): value is RefObject<HTMLElement | null> =>
  !!value && typeof value === 'object' && 'current' in value && !(value instanceof HTMLElement);

const resolveScrollElement = (scrollElement?: ScrollTarget): HTMLElement | Window | null => {
  if (!scrollElement) {
    return null;
  }
  if (typeof scrollElement === 'string') {
    return document.querySelector(scrollElement) as HTMLElement | null;
  }
  if (isRefObject(scrollElement)) {
    return scrollElement.current;
  }
  return scrollElement as HTMLElement | Window;
};

const getGlobalHeaderHeight = (): number => {
  const header = document.querySelector('.o-global-header') as HTMLElement | null;
  if (!header) {
    return 0;
  }
  return Math.round(header.getBoundingClientRect().height);
};

export const ShortcutItem: React.FC<ShortcutItemProps> = ({
  title,
  subtitle,
  onClick,
  scrollElement,
  modifiers,
  offset = 0,
}) => {
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      const element = resolveScrollElement(scrollElement);

      if (element && element !== window && element instanceof HTMLElement) {
        // Resolve at click time so mobile/tablet refs & sticky header height are correct
        const headerOffset = getGlobalHeaderHeight();
        const topPos = window.pageYOffset + element.getBoundingClientRect().top - headerOffset + offset;
        window.scrollTo({ top: Math.max(0, topPos), left: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }

      if (onClick) {
        onClick(event);
      }
    },
    [offset, onClick, scrollElement]
  );

  return (
    <div className={mapModifiers('a-shortcut-item', modifiers)}>
      <button className="a-shortcut-item__button" type="button" onClick={handleClick}>
        <h3 className="a-shortcut-item__title">{title}</h3>
        <p className="a-shortcut-item__subtitle">{subtitle}</p>
        {modifiers !== 'concept' && (
          <div className="a-shortcut-item__arrow">
            <img data-src={arrow} alt="click here" className="lazyload" width="14" height="20" />
          </div>
        )}
      </button>
    </div>
  );
};
